import type { FC, FormEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import Link from 'next/link'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { cn } from '@/lib/utils'
import { navLinks, studioMeta } from '@/constants/site'
import { socials, legalLinks } from '@/constants/socials'
import { NomaMark } from '@/components/generals/noma-mark'
import { Wordmark } from '@/components/generals/wordmark'
import { RollText } from '@/components/generals/roll-link'
import { ContactPill } from '@/components/generals/contact-pill'

type Status = 'idle' | 'sending' | 'done' | 'error'

export const SiteFooter: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)
    const [email, setEmail] = useState('')
    const [agreed, setAgreed] = useState(false)
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        /* The footer rises out from under the last section rather than simply
           scrolling into view, which is what gives the page its closing beat. */
        gsap.fromTo('[data-footer-lift]',
            { yPercent: -12 },
            {
                yPercent: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: root,
                    start: 'top bottom',
                    end: 'top 40%',
                    scrub: 0.55
                }
            }
        )

        gsap.fromTo('[data-footer-item]',
            { opacity: 0, y: 34 },
            {
                opacity: 1,
                y: 0,
                duration: 1,
                stagger: 0.06,
                ease: 'noma',
                scrollTrigger: {
                    trigger: root,
                    start: 'top 78%',
                    once: true
                }
            }
        )
    }, scope, [])

    const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (status === 'sending') {
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            })

            const data = await response.json()

            if (!response.ok) {
                throw new Error(data?.message ?? 'Something went wrong.')
            }

            setStatus('done')
            setMessage(data.message ?? 'You are on the list. Look out for the next letter.')
            setEmail('')
            setAgreed(false)
        } catch (error) {
            setStatus('error')
            setMessage(error instanceof Error ? error.message : 'Something went wrong.')
        }
    }

    return (
        <footer
            ref={scope}
            id='footer'
            className='relative z-10 overflow-hidden bg-[#f7f7f7] py-10 text-ink lg:py-12'
        >
            <div
                data-footer-lift
                className='mx-auto flex max-w-[1920px] flex-col gap-9 px-5 lg:gap-12 lg:px-[33px]'
            >
                {/* Top: mark + nav repeat */}
                <div className='grid grid-cols-2 gap-5 lg:grid-cols-4'>
                    <Link
                        href='/'
                        data-footer-item
                        data-cursor='link'
                        aria-label='Noma Studio, home'
                        className='group/roll flex h-fit w-fit items-center gap-2 text-ink'
                    >
                        <NomaMark className='size-[15px] transition-colors duration-500 group-hover/roll:text-brand' />

                        <RollText label='Noma' className='t-display pb-[0.06em] text-[19px] leading-none' />
                    </Link>

                    <nav
                        className='col-span-1 flex flex-wrap items-start justify-end gap-x-8 gap-y-3 lg:col-span-3'
                        aria-label='Footer'
                    >
                        {navLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                data-footer-item
                                data-cursor='link'
                                className='group/roll flex items-start gap-1 text-ink transition-colors duration-300 hover:text-brand'
                            >
                                <RollText label={link.label} className='t-cond pb-[0.08em] text-[17px] leading-none lg:text-[23px]' />

                                {link.count
                                    ? (
                                        <span className='t-mono mt-[2px] text-[10px] text-brand'>
                                            {link.count}
                                        </span>
                                    )
                                    : null}
                            </Link>
                        ))}
                    </nav>
                </div>

                {/* Middle: copyright, wordmark, newsletter */}
                <div className='hair-t grid gap-8 pt-9 lg:grid-cols-4'>
                    <span data-footer-item className='t-cond-soft text-[12px] leading-[1.3] text-ash'>
                        <span className='block'>
                            2026 Noma
                            {' '}
                            <span className='text-[1.2em] leading-none'>
                                {studioMeta.mark}
                            </span>
                        </span>

                        <span className='block'>
                            All rights reserved.
                        </span>
                    </span>

                    <div className='flex flex-col gap-8 lg:col-span-3'>
                        <Wordmark />

                        <div className='flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between'>
                            <p
                                data-footer-item
                                className='t-cond max-w-[52ch] text-[17px] leading-[1.15] text-ash lg:text-[23px]'
                            >
                                <span className='text-ink'>
                                    A letter, once a month.
                                </span>
                                {' '}
                                New work from the studio, fresh thinking from the founder, the occasional opinion.
                                {' '}
                                <span className='text-ink'>
                                    No filler. Subscribe below.
                                </span>
                            </p>

                            <form
                                data-footer-item
                                onSubmit={onSubmit}
                                className='flex w-full max-w-[420px] flex-col gap-3'
                            >
                                <div className='flex items-center gap-2 rounded-[var(--radius-field)] border border-[#9c9c9c26] bg-paper p-1.5 pl-4 transition-colors duration-500 focus-within:border-brand'>
                                    <label htmlFor='footer-email' className='sr-only'>
                                        Your email address
                                    </label>

                                    <input
                                        id='footer-email'
                                        type='email'
                                        name='email'
                                        required
                                        value={email}
                                        onChange={(event) => setEmail(event.target.value)}
                                        placeholder='Enter your email'
                                        autoComplete='email'
                                        className='t-cond-soft w-full bg-transparent text-[14px] text-ink outline-none placeholder:text-ash'
                                    />

                                    <button
                                        type='submit'
                                        data-cursor='link'
                                        disabled={status === 'sending' || !agreed}
                                        className='group/roll t-cond shrink-0 rounded-[var(--radius-field)] bg-ink px-4 py-2.5 text-[14px] text-paper transition-opacity duration-300 disabled:opacity-40'
                                    >
                                        <RollText label={status === 'sending' ? 'Sending' : status === 'done' ? 'Joined' : 'Join Now'} />
                                    </button>
                                </div>

                                <label className='flex cursor-pointer items-start gap-2.5'>
                                    <input
                                        type='checkbox'
                                        required
                                        checked={agreed}
                                        onChange={(event) => setAgreed(event.target.checked)}
                                        className='mt-[2px] size-3.5 shrink-0 cursor-pointer accent-brand'
                                    />

                                    <span
                                        className={cn(
                                            't-cond-soft text-[13px] leading-[1.4]',
                                            status === 'error' ? 'text-brand' : 'text-ash'
                                        )}
                                        role={status === 'idle' ? undefined : 'status'}
                                    >
                                        {message || (
                                            <>
                                                By signing up, you are agreeing to our
                                                {' '}
                                                <Link href='/legal/privacy' className='text-ink underline underline-offset-2'>
                                                    privacy policy.
                                                </Link>
                                            </>
                                        )}
                                    </span>
                                </label>
                            </form>
                        </div>
                    </div>
                </div>

                {/* Bottom: socials, legal, contact */}
                <div className='grid-rules grid grid-cols-2 gap-8 lg:grid-cols-4'>
                    <div data-footer-item className='flex h-fit items-center gap-4'>
                        {socials.map((social) => (
                            <a
                                key={social.label}
                                href={social.href}
                                target='_blank'
                                rel='noreferrer'
                                data-cursor='link'
                                aria-label={social.label}
                                className='text-ink transition-colors duration-300 hover:text-brand'
                            >
                                <svg viewBox='0 0 24 24' className='size-[18px]' aria-hidden='true'>
                                    <path d={social.path} fill='currentColor' />
                                </svg>
                            </a>
                        ))}
                    </div>

                    <div data-footer-item className='flex flex-col gap-2'>
                        <span className='t-mono text-ash'>
                            legal
                        </span>

                        {legalLinks.map((link) => (
                            <Link
                                key={link.href}
                                href={link.href}
                                data-cursor='link'
                                className='group/roll t-mono w-fit text-ink transition-colors duration-300 hover:text-brand'
                            >
                                <RollText label={link.label} />
                            </Link>
                        ))}
                    </div>

                    <div
                        data-footer-item
                        className='col-span-2 flex items-center justify-end gap-4'
                    >
                        <span className='t-mono text-ash'>
                            let&apos;s talk
                        </span>

                        <ContactPill />
                    </div>
                </div>
            </div>
        </footer>
    )
}
